import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Step1Request } from '@/dtos/FlowClinicalSessions/index';
import { BasePerson } from '@/dtos/Persons/index';
import { useProcessStep1 } from '@/hooks/FlowClinical';
import { useGetPersons, useGetPersonsPaged } from '@/hooks/Person';
import { zodResolver } from '@hookform/resolvers/zod';
import { Search, User, UserPlus } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import GdprConsent from './GdprConsent';
import HealthProfileForm from './HealthProfileForm';
import PersonCreateForm from './PersonCreateForm';
import PersonSearch from './PersonSearch';
import SelectedPersonDisplay from './SelectedPersonDisplay';

// Form schemas
const personSchema = z.object({
  firstName: z.string().min(1, 'Prenumele este obligatoriu'),
  lastName: z.string().min(1, 'Numele este obligatoriu'),
  email: z.string().email('Email invalid').optional().or(z.literal('')),
  phone: z.string().optional(),
  cnp: z.string().optional(),
  gender: z.string().optional(),
  birthDate: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  address: z.string().optional(),
});

const healthProfileSchema = z.object({
  isSmoker: z.boolean(),
  isFormerSmoker: z.boolean(),
  hasDiabetes: z.boolean(),
  hasHypertension: z.boolean(),
  hasDyslipidemia: z.boolean(),
  bodyMassIndex: z.coerce.number().optional(),
  systolicBloodPressure: z.coerce.number().optional(),
  familyHistoryCardiovascularDisease: z.boolean(),
});

type PersonFormValues = z.infer<typeof personSchema>;
type HealthProfileFormValues = z.infer<typeof healthProfileSchema>;

const defaultHealthProfileData: HealthProfileFormValues = {
  isSmoker: false,
  isFormerSmoker: false,
  hasDiabetes: false,
  hasHypertension: false,
  hasDyslipidemia: false,
  bodyMassIndex: undefined,
  systolicBloodPressure: undefined,
  familyHistoryCardiovascularDisease: false,
};

export interface Step1Handle {
  submit: () => Promise<void>;
  canSubmit: () => boolean;
}

interface Step1Props {
  sessionId?: string;
  isEditMode: boolean;
  selectedPerson: BasePerson | null;
  haveGdpr: boolean;
  personIdFromSession?: string | null; // For auto-selecting person in edit mode
  onGdprChange: (checked: boolean) => void;
  onPersonSelect: (person: BasePerson) => void;
  onPersonDeselect: () => void;
  onSuccess: (sessionId: string) => void;
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

const Step1 = forwardRef<Step1Handle, Step1Props>(
  (
    {
      sessionId,
      isEditMode,
      selectedPerson,
      haveGdpr,
      personIdFromSession,
      onGdprChange,
      onPersonSelect,
      onPersonDeselect,
      onSuccess,
      onCanSubmitChange,
    },
    ref,
  ) => {
    const processStep1Mutation = useProcessStep1();
    const [personSearchTerm, setPersonSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [personMode, setPersonMode] = useState<'search' | 'create'>('search');

    // Person search
    const {
      data: personsResponse,
      isLoading: isLoadingPersons,
      refetch: refetchPersons,
    } = useGetPersonsPaged(1, 10, personSearchTerm || undefined);
    const searchResults = personsResponse?.isSuccess ? personsResponse.data?.items || [] : [];

    // Get all persons to find by personId (for auto-selection in edit mode)
    const { data: allPersonsResponse } = useGetPersons();
    const allPersons = allPersonsResponse?.isSuccess ? allPersonsResponse.data || [] : [];

    // Auto-select person when personIdFromSession is provided and person not yet selected
    useEffect(() => {
      if (personIdFromSession && !selectedPerson && allPersons.length > 0) {
        const person = allPersons.find((p) => p.id === personIdFromSession);
        if (person) {
          onPersonSelect(person);
        }
      }
    }, [personIdFromSession, selectedPerson, allPersons, onPersonSelect]);

    // Also search in table results for the person
    useEffect(() => {
      if (personIdFromSession && !selectedPerson && searchResults.length > 0) {
        const person = searchResults.find((p) => p.id === personIdFromSession);
        if (person) {
          onPersonSelect(person);
        }
      }
    }, [personIdFromSession, selectedPerson, searchResults, onPersonSelect]);

    // Forms
    const personForm = useForm<PersonFormValues>({
      resolver: zodResolver(personSchema),
      defaultValues: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        cnp: '',
        gender: '',
        birthDate: '',
        country: '',
        city: '',
        address: '',
      },
    });

    // Initialize health profile form with selectedPerson's data if available
    const healthProfileForm = useForm<HealthProfileFormValues>({
      resolver: zodResolver(healthProfileSchema),
      defaultValues: selectedPerson?.healthProfile
        ? {
            isSmoker: selectedPerson.healthProfile.isSmoker ?? false,
            isFormerSmoker: selectedPerson.healthProfile.isFormerSmoker ?? false,
            hasDiabetes: selectedPerson.healthProfile.hasDiabetes ?? false,
            hasHypertension: selectedPerson.healthProfile.hasHypertension ?? false,
            hasDyslipidemia: selectedPerson.healthProfile.hasDyslipidemia ?? false,
            bodyMassIndex: selectedPerson.healthProfile.bodyMassIndex,
            systolicBloodPressure: selectedPerson.healthProfile.systolicBloodPressure,
            familyHistoryCardiovascularDisease:
              selectedPerson.healthProfile.familyHistoryCardiovascularDisease ?? false,
          }
        : defaultHealthProfileData,
    });

    // Track the last person ID we populated the form for
    const lastPopulatedPersonIdRef = useRef<string | null>(null);

    // Populate health profile form when person is selected or changes
    // Only reset if it's a different person (by ID), not when the same person object is updated
    useEffect(() => {
      if (!selectedPerson) {
        lastPopulatedPersonIdRef.current = null;
        return;
      }

      const currentPersonId = selectedPerson.id;

      // Only reset form if it's a different person (person ID changed)
      // This prevents resetting the form when the same person is updated from session refetch
      if (lastPopulatedPersonIdRef.current !== currentPersonId) {
        if (selectedPerson.healthProfile) {
          const hp = selectedPerson.healthProfile;
          healthProfileForm.reset({
            isSmoker: hp.isSmoker ?? false,
            isFormerSmoker: hp.isFormerSmoker ?? false,
            hasDiabetes: hp.hasDiabetes ?? false,
            hasHypertension: hp.hasHypertension ?? false,
            hasDyslipidemia: hp.hasDyslipidemia ?? false,
            bodyMassIndex: hp.bodyMassIndex,
            systolicBloodPressure: hp.systolicBloodPressure,
            familyHistoryCardiovascularDisease: hp.familyHistoryCardiovascularDisease ?? false,
          });
        } else {
          // Person selected but no health profile - reset to defaults
          healthProfileForm.reset(defaultHealthProfileData);
        }
        lastPopulatedPersonIdRef.current = currentPersonId;
      }
    }, [selectedPerson?.id, selectedPerson?.healthProfile, healthProfileForm]);

    // Handle person search
    const handlePersonSearch = () => {
      if (personSearchTerm.trim()) {
        setIsSearching(true);
        refetchPersons().finally(() => setIsSearching(false));
      }
    };

    // Handle person selection
    const handleSelectPerson = (person: BasePerson) => {
      onPersonSelect(person);
      setPersonMode('search');
    };

    // Handle submit
    const handleSubmit = async () => {
      if (!haveGdpr) {
        return;
      }

      const healthProfileValues = healthProfileForm.getValues();
      let request: Step1Request;

      if (selectedPerson) {
        // Case 1: Existing person selected - always send health profile for update
        request = {
          sessionId: sessionId || null,
          personInternalId: selectedPerson.personInternalId || undefined,
          haveGdpr: true,
          healthProfile: {
            isSmoker: healthProfileValues.isSmoker,
            isFormerSmoker: healthProfileValues.isFormerSmoker,
            hasDiabetes: healthProfileValues.hasDiabetes,
            hasHypertension: healthProfileValues.hasHypertension,
            hasDyslipidemia: healthProfileValues.hasDyslipidemia,
            bodyMassIndex: healthProfileValues.bodyMassIndex,
            systolicBloodPressure: healthProfileValues.systolicBloodPressure,
            familyHistoryCardiovascularDisease: healthProfileValues.familyHistoryCardiovascularDisease,
          },
        };
      } else {
        // Case 2: Create new person
        const personValid = await personForm.trigger();
        if (!personValid) return;

        const personValues = personForm.getValues();
        request = {
          sessionId: sessionId || null,
          haveGdpr: true,
          person: {
            firstName: personValues.firstName,
            lastName: personValues.lastName,
            email: personValues.email || undefined,
            phone: personValues.phone || undefined,
            cnp: personValues.cnp || undefined,
            gender: personValues.gender || undefined,
            birthDate: personValues.birthDate || undefined,
            country: personValues.country || undefined,
            city: personValues.city || undefined,
            address: personValues.address || undefined,
            healthProfile: {
              isSmoker: healthProfileValues.isSmoker,
              isFormerSmoker: healthProfileValues.isFormerSmoker,
              hasDiabetes: healthProfileValues.hasDiabetes,
              hasHypertension: healthProfileValues.hasHypertension,
              hasDyslipidemia: healthProfileValues.hasDyslipidemia,
              bodyMassIndex: healthProfileValues.bodyMassIndex,
              systolicBloodPressure: healthProfileValues.systolicBloodPressure,
              familyHistoryCardiovascularDisease: healthProfileValues.familyHistoryCardiovascularDisease,
            },
          },
        };
      }

      const result = await processStep1Mutation.mutateAsync(request);
      if (result.isSuccess && result.data) {
        onSuccess(result.data.sessionId);
      }
    };

    const showPersonSection = isEditMode || haveGdpr;

    const computeCanSubmit = () => {
      if (!haveGdpr) return false;
      if (selectedPerson) return true;
      if (personMode === 'create') return true;
      return false;
    };

    // Keep parent in sync with current Step1 submit eligibility.
    useEffect(() => {
      onCanSubmitChange?.(computeCanSubmit());
    }, [haveGdpr, selectedPerson, personMode, onCanSubmitChange]);

    // Expose submit handler via ref
    useImperativeHandle(
      ref,
      () => ({
        submit: handleSubmit,
        canSubmit: computeCanSubmit,
      }),
      [haveGdpr, selectedPerson, personMode],
    );

    return (
      <div className='space-y-6'>
        {/* GDPR Consent */}
        <GdprConsent haveGdpr={haveGdpr} isEditMode={isEditMode} onGdprChange={onGdprChange} />

        {/* Person Selection / Creation */}
        {showPersonSection && (
          <>
            {/* Selected Person Display */}
            {selectedPerson && (
              <SelectedPersonDisplay person={selectedPerson} isEditMode={isEditMode} onDeselect={onPersonDeselect} />
            )}

            {/* Person Mode Toggle */}
            {!selectedPerson && (
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-lg'>
                    <User className='h-5 w-5' />
                    Identificare Pacient
                  </CardTitle>
                  <CardDescription>Căutați un pacient existent sau creați unul nou.</CardDescription>
                </CardHeader>
                <CardContent className='space-y-4'>
                  {/* Mode Toggle Buttons */}
                  <div className='flex gap-2'>
                    <Button
                      variant={personMode === 'search' ? 'default' : 'outline'}
                      onClick={() => setPersonMode('search')}
                      className='flex-1'
                    >
                      <Search className='h-4 w-4 mr-2' />
                      Caută Pacient Existent
                    </Button>
                    <Button
                      variant={personMode === 'create' ? 'default' : 'outline'}
                      onClick={() => setPersonMode('create')}
                      className='flex-1'
                    >
                      <UserPlus className='h-4 w-4 mr-2' />
                      Creează Pacient Nou
                    </Button>
                  </div>

                  <Separator />

                  {/* Search Mode */}
                  {personMode === 'search' && (
                    <PersonSearch
                      searchTerm={personSearchTerm}
                      onSearchTermChange={setPersonSearchTerm}
                      onSearch={handlePersonSearch}
                      searchResults={searchResults}
                      isLoading={isLoadingPersons}
                      isSearching={isSearching}
                      onSelectPerson={handleSelectPerson}
                      onCreateNew={() => setPersonMode('create')}
                    />
                  )}

                  {/* Create Mode */}
                  {personMode === 'create' && <PersonCreateForm form={personForm} />}
                </CardContent>
              </Card>
            )}

            {/* Health Profile */}
            {(selectedPerson || personMode === 'create') && (
              <HealthProfileForm form={healthProfileForm} selectedPerson={selectedPerson} />
            )}
          </>
        )}
      </div>
    );
  },
);

Step1.displayName = 'Step1';

export default Step1;
